'use client';

import * as React from 'react';
import { Checkbox as CheckboxPrimitive } from '@base-ui/react/checkbox';
import { Check, Minus } from 'lucide-react';

import { cn } from '@/lib/utils';

type CheckboxProps = Omit<CheckboxPrimitive.Root.Props, 'ref'> & {
  label?: React.ReactNode;
  labelClassName?: string;
  /** @deprecated Use onCheckedChange. Kept for backward compatibility (e.g. react-hook-form register). */
  onChange?: (e: { target: { checked: boolean } }) => void;
};

const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  CheckboxProps
>(
  (
    { className, label, labelClassName, onChange, onCheckedChange, ...props },
    ref
  ) => {
    const handleCheckedChange = (
      checked: boolean,
      eventDetails: CheckboxPrimitive.Root.ChangeEventDetails
    ) => {
      onCheckedChange?.(checked, eventDetails);
      onChange?.({ target: { checked } });
    };

    const root = (
      <CheckboxPrimitive.Root
        ref={ref}
        className={cn(
          // Opacity only dims the unchecked state, matching Switch: a
          // checked-and-disabled control should still read as clearly
          // checked, not washed out.
          'group peer inline-flex items-center justify-center h-4 w-4 shrink-0 rounded-sm border bg-background ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 data-disabled:cursor-not-allowed data-disabled:data-unchecked:opacity-50',
          'border-ds-border-subtler',
          'data-checked:bg-[var(--checkbox-checked)] data-checked:text-[var(--checkbox-checked-foreground)] data-checked:border-[var(--checkbox-checked)]',
          'data-indeterminate:bg-[var(--checkbox-checked)] data-indeterminate:text-[var(--checkbox-checked-foreground)] data-indeterminate:border-[var(--checkbox-checked)]',
          className
        )}
        onCheckedChange={handleCheckedChange}
        {...props}
      >
        <CheckboxPrimitive.Indicator
          className={cn('flex items-center justify-center text-current')}
        >
          <Check className="h-3.5 w-3.5 shrink-0 group-data-indeterminate:hidden" />
          <Minus className="h-3.5 w-3.5 shrink-0 hidden group-data-indeterminate:block" />
        </CheckboxPrimitive.Indicator>
      </CheckboxPrimitive.Root>
    );

    if (label !== undefined) {
      return (
        <label className="inline-flex items-center gap-2 cursor-pointer select-none">
          {root}
          <span className={cn('text-sm text-foreground', labelClassName)}>
            {label}
          </span>
        </label>
      );
    }

    return root;
  }
);
Checkbox.displayName = 'Checkbox';

export { Checkbox };
